import React from 'react';
import {
  Box,
  VStack,
  Text,
  Icon,
  Badge,
  HStack,
  Switch,
  FormControl,
  FormLabel,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  SliderMark,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useColorModeValue
} from '@chakra-ui/react';
import { FaCog } from 'react-icons/fa';

const AdvancedVoiceSettings = ({ 
  vapiConfig = {}, 
  voiceSettings = {}, 
  setVoiceSettings = () => {} 
}) => {
  const accentColor = 'blue.500';

  // Safe access with defaults
  const {
    voiceProvider = 'playht'
  } = vapiConfig;

  const {
    speed = 1.0,
    stability = 0.5,
    similarityBoost = 0.5,
    style = 0.0,
    useSpeakerBoost = true
  } = voiceSettings;

  // Get supported settings for current voice provider
  const getSupportedSettings = () => {
    switch (voiceProvider) {
      case 'elevenlabs':
        return ['speed', 'stability', 'similarityBoost', 'style', 'useSpeakerBoost'];
      case 'playht':
        return ['speed'];
      case 'openai':
        return ['speed'];
      default:
        return ['speed'];
    }
  };

  const supportedSettings = getSupportedSettings();

  return (
    <Accordion allowToggle>
      <AccordionItem>
        <AccordionButton>
          <Box flex="1" textAlign="left">
            <HStack>
              <Icon as={FaCog} />
              <Text fontWeight="bold">Advanced Voice Settings</Text>
              <Badge colorScheme="blue" ml={2}>
                {voiceProvider}
              </Badge>
            </HStack>
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          <VStack spacing={6} align="stretch">
            
            {/* Speech Speed - Supported by all providers */}
            {supportedSettings.includes('speed') && (
              <FormControl>
                <FormLabel>
                  Speech Speed: {speed}x
                  <Text fontSize="xs" color="gray.500" fontWeight="normal">
                    Controls how fast the AI speaks (0.25x - 4.0x)
                  </Text>
                </FormLabel>
                <Slider
                  value={speed}
                  onChange={(value) => setVoiceSettings(prev => ({...prev, speed: value}))}
                  min={0.25}
                  max={4.0}
                  step={0.25}
                  colorScheme="blue"
                >
                  <SliderMark value={0.25} mt={2} fontSize="xs">0.25x</SliderMark>
                  <SliderMark value={1.0} mt={2} fontSize="xs" ml={-2}>1.0x</SliderMark>
                  <SliderMark value={2.0} mt={2} fontSize="xs" ml={-2}>2.0x</SliderMark>
                  <SliderMark value={4.0} mt={2} fontSize="xs" ml={-4}>4.0x</SliderMark>
                  <SliderTrack>
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb />
                </Slider>
              </FormControl>
            )}

            {/* ElevenLabs specific settings */}
            {voiceProvider === 'elevenlabs' && (
              <>
                {/* Voice Stability */}
                <FormControl>
                  <FormLabel>
                    Voice Stability: {stability}
                    <Text fontSize="xs" color="gray.500" fontWeight="normal">
                      Higher values = more stable/consistent voice (0.0 - 1.0)
                    </Text>
                  </FormLabel>
                  <Slider
                    value={stability}
                    onChange={(value) => setVoiceSettings(prev => ({...prev, stability: value}))}
                    min={0.0}
                    max={1.0}
                    step={0.1}
                    colorScheme="green"
                  >
                    <SliderMark value={0.0} mt={2} fontSize="xs">0.0</SliderMark>
                    <SliderMark value={0.5} mt={2} fontSize="xs" ml={-2}>0.5</SliderMark>
                    <SliderMark value={1.0} mt={2} fontSize="xs" ml={-2}>1.0</SliderMark>
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Similarity Boost */}
                <FormControl>
                  <FormLabel>
                    Similarity Boost: {similarityBoost}
                    <Text fontSize="xs" color="gray.500" fontWeight="normal">
                      Higher values = more similar to original voice (0.0 - 1.0)
                    </Text>
                  </FormLabel>
                  <Slider
                    value={similarityBoost}
                    onChange={(value) => setVoiceSettings(prev => ({...prev, similarityBoost: value}))}
                    min={0.0}
                    max={1.0}
                    step={0.1}
                    colorScheme="purple"
                  >
                    <SliderMark value={0.0} mt={2} fontSize="xs">0.0</SliderMark>
                    <SliderMark value={0.5} mt={2} fontSize="xs" ml={-2}>0.5</SliderMark>
                    <SliderMark value={1.0} mt={2} fontSize="xs" ml={-2}>1.0</SliderMark>
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Speaking Style */}
                <FormControl>
                  <FormLabel>
                    Speaking Style: {style}
                    <Text fontSize="xs" color="gray.500" fontWeight="normal">
                      Higher values = more expressive/emotional speech (0.0 - 1.0)
                    </Text>
                  </FormLabel>
                  <Slider
                    value={style}
                    onChange={(value) => setVoiceSettings(prev => ({...prev, style: value}))}
                    min={0.0}
                    max={1.0}
                    step={0.1}
                    colorScheme="orange"
                  >
                    <SliderMark value={0.0} mt={2} fontSize="xs">0.0</SliderMark>
                    <SliderMark value={0.5} mt={2} fontSize="xs" ml={-2}>0.5</SliderMark>
                    <SliderMark value={1.0} mt={2} fontSize="xs" ml={-2}>1.0</SliderMark>
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Speaker Boost */}
                <FormControl>
                  <FormLabel>Speaker Boost</FormLabel>
                  <HStack>
                    <Switch
                      isChecked={useSpeakerBoost}
                      onChange={(e) => setVoiceSettings(prev => ({...prev, useSpeakerBoost: e.target.checked}))}
                      colorScheme="red"
                      size="lg"
                    />
                    <Text fontSize="sm">
                      {useSpeakerBoost ? 'Enhanced audio quality (uses more credits)' : 'Standard audio quality'}
                    </Text>
                  </HStack>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Improves audio quality but increases cost
                  </Text>
                </FormControl>
              </>
            )}

            {/* Settings Summary */}
            <Box p={4} bg="gray.50" borderRadius="md" borderLeft="4px solid" borderLeftColor={accentColor}>
              <Text fontWeight="bold" fontSize="sm" mb={2}>Current Voice Settings Summary:</Text>
              <VStack align="start" spacing={1} fontSize="xs">
                <Text>🎵 Speed: {speed}x</Text>
                {voiceProvider === 'elevenlabs' && (
                  <>
                    <Text>🎯 Stability: {stability}</Text>
                    <Text>🔗 Similarity: {similarityBoost}</Text>
                    <Text>🎭 Style: {style}</Text>
                    <Text>🔊 Speaker Boost: {useSpeakerBoost ? 'On' : 'Off'}</Text>
                  </>
                )}
              </VStack>
            </Box>
          </VStack>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

export default AdvancedVoiceSettings;